import { useEffect, useReducer, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useFlags } from "launchdarkly-react-client-sdk";

import { Spacing, MediaQueries, Colors } from "@paclife/plds.tokens";

import Card from "common/Card/Card";
import TextField from "common/TextField/TextField";
import Button from "common/Button/Button";
import Checkbox from "common/Checkbox/Checkbox";
import ErrorMessage from "common/Message/Message";
import Typography from "common/Typography/Typography";
import ConfirmInviteModal from "components/ConfirmInviteModal/ConfirmInviteModal";

import { useTeamContext } from "pages/MyTeam/MyTeam";
import validator from "utils/validator";
import { inviteTeamMember } from "api/team";


const CustomGrid = styled.div`
    @media (min-width: ${MediaQueries.md}) {
        display: grid;
        grid-template-columns: repeat(2, minmax(150px, 1fr));
        column-gap: 12px;
    }
`;

const GridItem = styled.div`
    margin-bottom: ${Spacing[24]};
    @media (min-width: ${MediaQueries.md}) {
        &.full-width {
            grid-column: 1/3;
        }
        &.error-message {
            grid-column: 1/3;
        }
    }
`;

const InviteButton = styled(Button)`
    width: 100%;
    @media (min-width: ${MediaQueries.md}) {
        width: 120px;
        grid-column: 1/3;
        justify-self: end;
    }
`;

const Notice = styled(Typography)`
    margin-bottom: ${Spacing[16]};
`;

const LookupAnchor = styled.a`
    margin-top: ${Spacing[8]};
    color: rgb(13, 101, 179);
    text-decoration: underline;
`;

const AppNote = styled.span`
    font-weight: 400;
    font-size: 0.8rem;
    line-height: 0.8rem;
    color: ${Colors.blue600}
`;


const DEFAULT_STATE = {
    firstName: "",
    lastName: "",
    emailAddress: "",
    confirmEmail: "",
    appointed: false,
    acknowledge: false, 
    npn: ""
};

const UPDATE = "UPDATE";
const RESET = "RESET";
const reducer = (state, action) => {
    switch (action.type) {
        case UPDATE:
            return {
                ...state,
                ...action.payload
            };
        case RESET:
            return DEFAULT_STATE;
        default:
            throw Error("Unhandled Action Type");
    }
};

const InviteAssistant = () => {
    const { t } = useTranslation("manMyTeam");
    const { setTeam } = useTeamContext();
    const [assistant, dispatch] = useReducer(reducer, DEFAULT_STATE);
    const [errors, setErrors] = useState({
        emailAddress: false,
        confirmEmail: false, 
        npn: false
    });

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [disableBtn, setDisableBtn] = useState(true);
    const [loadingBtn, setLoadingBtn] = useState(false);
    const [inviteError, setInviteError] = useState(false);
    const { rsdUpReadonlyaccessEn } = useFlags();

    useEffect(() => {
        if (assistant.emailAddress === "") {
            return false;
        }
        const isValidEmail = validator.Email(assistant.emailAddress);
        setErrors((err) => ({
            ...err,
            emailAddress: !isValidEmail
        }));
    }, [assistant.emailAddress]);

    useEffect(() => {
        if (assistant.npn === "") {
            return false;
        }
        const isValidNpn = validator.NPN(assistant.npn);
        setErrors((err) => ({
            ...err,
            npn: !isValidNpn
        }));
    }, [assistant.npn]);

    useEffect(() => {
        if (assistant.confirmEmail === "") {
            return false;
        }
        const sameEmail =
            assistant.emailAddress.toLowerCase() ===
            assistant.confirmEmail.toLowerCase();
        setErrors((err) => ({
            ...err,
            confirmEmail: !sameEmail
        }));
    }, [assistant]);

    useEffect(() => {
        if (
            assistant.firstName === "" ||
            assistant.lastName === "" ||
            assistant.emailAddress === "" ||
            assistant.confirmEmail === "" ||
            assistant.acknowledge === false ||
            errors.emailAddress === true ||
            errors.confirmEmail === true ||
            errors.npn === true
        ) {            
            setDisableBtn(true);
        } else {
            if (assistant.appointed === true) {
                if (assistant.npn !== "") {
                    setDisableBtn(false);
                } else {
                    setDisableBtn(true);
                }
            } else {
                setDisableBtn(false);
            }
        }
    }, [assistant, errors]);

    const handleChange = (e) => {
        const { name, checked, value } = e.target;
        let val = value.trim();
        if (name === "acknowledge" || name === "appointed") {
            val = checked;
        }
        dispatch({ type: UPDATE, payload: { [name]: val } });
    };

    const handlePaste = (e) => {
        e.preventDefault();
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        if (rsdUpReadonlyaccessEn) {
            return;
        }
        setLoadingBtn(true);

        const payload = {
            ...assistant,
            status: "",
            lastLoginDateTime: ""
        };
        const { succeed, message, data } = await inviteTeamMember(payload);
        if (succeed) {
            setInviteError(false);
            dispatch({ type: RESET });
            setTeam((prev) => {
                const curr = [...prev];
                curr.unshift({
                    id: data.id,
                    name: `${assistant.firstName} ${assistant.lastName}`,
                    emailAddress: assistant.emailAddress,
                    status: "Invited",
                    lastActive: "-"
                });
                return curr;
            });
        } else {
            setInviteError(message);
        }
        setLoadingBtn(false);
    };

    return (
        <div id="invite">
            <Card title={t("invite.title")}>
                <Notice variant="microcopy" display="block">
                    {t("invite.notice")}
                </Notice>
                <CustomGrid>
                    <GridItem>
                        <TextField
                            label="First Name"
                            name="firstName"
                            value={assistant.firstName}
                            onChange={handleChange}
                            maxLength={25}
                        />
                    </GridItem>
                    <GridItem>
                        <TextField
                            label="Last Name"
                            name="lastName"
                            value={assistant.lastName}
                            onChange={handleChange}
                            maxLength={35}
                        />
                    </GridItem>
                    <GridItem>
                        <TextField
                            label="Email"
                            name="emailAddress"
                            value={assistant.emailAddress}
                            onChange={handleChange}
                            error={errors.emailAddress}
                            errorMessage="Invalid Email Address"
                            maxLength={60}
                        />
                    </GridItem>
                    <GridItem>
                        <TextField
                            label="Confirm Email"
                            name="confirmEmail"
                            value={assistant.confirmEmail}
                            onChange={handleChange}
                            onPaste={handlePaste}
                            error={errors.confirmEmail}
                            errorMessage="Emails do not match"
                            maxLength={60}
                        />
                    </GridItem>
                    <GridItem>
                        <Checkbox
                            id="appointed"
                            label={
                                assistant.appointed
                                    ? <>
                                        { t("invite.appointed") }
                                        <br/><AppNote>{ t("invite.appointedNote") }</AppNote>
                                    </>
                                    : t("invite.appointed")
                            }
                            name="appointed"
                            checked={assistant.appointed}
                            onChange={handleChange}
                            nowrap="true"
                        />
                    </GridItem>
                    <GridItem>
                        { assistant.appointed &&
                            <>
                                <TextField
                                    label="NPN"
                                    name="npn"
                                    value={assistant.npn}
                                    onChange={handleChange}
                                    onPaste={handlePaste}
                                    error={errors.npn}
                                    errorMessage="NPN not valid"
                                    maxLength={10}
                                />
                                <LookupAnchor href="https://nipr.com/help/look-up-your-npn" target="_blank">
                                    NPN Lookup
                                </LookupAnchor>
                            </>
                        }
                    </GridItem>
                    <GridItem className="full-width">
                        <Checkbox
                            id="acknowledge"
                            label={t("invite.acknowledge")}
                            name="acknowledge"
                            checked={assistant.acknowledge}
                            onChange={handleChange}
                        />
                    </GridItem>
                    {inviteError && (
                        <GridItem className="error-message">
                            <ErrorMessage type="error" message={inviteError} />
                        </GridItem>
                    )}
                    <InviteButton
                        type="button"
                        disabled={disableBtn || rsdUpReadonlyaccessEn}
                        loading={loadingBtn}
                        onClick={handleInvite}
                        data-track="analytics"
                        data-bname="invite-team-member"
                    >
                        {t("button.invite")}
                    </InviteButton>
                </CustomGrid>
                {showConfirmModal && (
                    <ConfirmInviteModal
                        closeModal={() => setShowConfirmModal(false)}
                        name="Harry Potter"
                    />
                )}
            </Card>
        </div>
    );
};

export default InviteAssistant;
